import React, { useState } from 'react';
import { asUserAuthor } from 'domain/shared/author';
import { NoteCommentDTO } from 'types/company/communityNotes';
import QuickEditField from 'ui/elements/form/QuickEditField';
import { getOrUndefined } from 'util/resource';
import { useSelfUserProfile } from 'apis/CompanyAPI/users/useSelfUserProfile';
import Timestamp from 'ui/elements/Timestamp';
import EditIcon from 'ui/elements/icons/EditIcon';
import IconButton from 'ui/elements/icons/IconButton';
import UserProfileDialog from 'domain/users/UserProfile/UserProfileDialog';

interface Props {
  comment: NoteCommentDTO;
  onEdit?: (commentId: string, text: string) => Promise<void>;
}

export default function NoteComment({ comment, onEdit }: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(comment.text);
  const [userDialogId, setUserDialogId] = useState('');
  const { resource: userProfileResource } = useSelfUserProfile();
  const userProfile = getOrUndefined(userProfileResource);

  const isAuthor = userProfile?.cwUserId === comment.author.cwUserId;
  const author = asUserAuthor({
    cwUserId: comment.author.cwUserId,
    name: comment.author.name || undefined,
    imageUrl: comment.author.imageUrl || undefined,
  });

  const onAuthorClick = comment.author.cwUserId ? () => setUserDialogId(comment.author.cwUserId) : undefined;

  const handleStartEditing = () => {
    setEditedText(comment.text);
    setIsEditing(true);
  };

  const handleEdit = async (newText: string) => {
    if (onEdit && newText.trim().length > 0) {
      await onEdit(comment.id, newText);
      setIsEditing(false);
    }
  };

  return (
    <>
      <div className="u-flex u-flex--justify-between u-flex--gap-half">
        <div className="u-flex-grow">
          <div>
            {isEditing && isAuthor && onEdit ? (
              <QuickEditField
                name="comment-text"
                value={editedText}
                onChange={e => setEditedText(e.target.value)}
                onCancel={() => {
                  setEditedText(comment.text);
                  setIsEditing(false);
                }}
                onConfirm={handleEdit}
                placeholder="Edit your comment"
                minRows={2}
                maxRows={6}
              />
            ) : (
              <p>{comment.text}</p>
            )}
          </div>
          <div
            className="text-tiny text-grey u-quarter-spacing-top u-flex--gap-quarter u-flex-align-center"
            style={{ lineHeight: 1 }}
          >
            <button onClick={onAuthorClick}>{author.name}</button>{' '}
            <Timestamp timestamp={comment.createdAt} tooltipPlacement="bottom" />
          </div>
        </div>
        {isAuthor && onEdit && !isEditing && (
          <div>
            <IconButton color="indigo" onClick={handleStartEditing} size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          </div>
        )}
      </div>
      <UserProfileDialog isOpen={!!userDialogId} cwUserId={userDialogId} onClose={() => setUserDialogId('')} />
    </>
  );
}
